import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useStatus } from '../../context/StatusContext';
import { useDataUpdates } from '../../hooks/useDataUpdates';
import { useDataCache } from '../../hooks/useDataCache';
import { EntityCellRenderer, NetworkCellRenderer, NodeStatusCellRenderer } from '../../utils/metrics';
import { StatusEvaluator } from '../../context/StatusEvaluator';
import { NODE_THRESHOLDS, EXTERNAL_VALIDATION_THRESHOLDS } from '../../config/thresholds';

import LoadingAnimation from '../../components/loadingAnimation';
import '../../styles/Nodes.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getApiBase } from '../../utils/apiBase';

function NodesMobile() {
  const { getCachedData, setCachedData } = useDataCache();
  const [nodeData, setNodeData] = useState(() => {
    try { return getCachedData('/node_info.json') || {}; } catch { return {}; }
  });
  const [nodeHeads, setNodeHeads] = useState(() => {
    try { return getCachedData('/node_heads.json') || {}; } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMode, setDisplayMode] = useState('chain');
  const { updatePageStatus } = useStatus();
  const gridApiRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const [rowData, setRowData] = useState([]);
  const [nodeDbInfo, setNodeDbInfo] = useState(() => {
    try { return getCachedData('/node_db_info.json') || {}; } catch { return {}; }
  });

  const fetchNodeHeads = useCallback(() => {
    const API = getApiBase();
    return fetch(`${API}/node_heads.json`)
      .then(r => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') {
          setNodeHeads(data);
          setCachedData('/node_heads.json', data);
        }
        return data || {};
      })
      .catch(() => ({}));
  }, [setCachedData]);

  const fetchNodeDbInfo = useCallback(() => {
    const API = getApiBase();
    return fetch(`${API}/node_db_info.json`)
      .then(r => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') {
          setNodeDbInfo(data);
          setCachedData('/node_db_info.json', data);
        }
        return data || {};
      })
      .catch(() => ({}));
  }, [setCachedData]);

  const fetchData = useCallback(() => {
    console.log('Fetching node data at:', new Date().toISOString());
    if (isInitialLoadRef.current) setLoading(true);
    setError(null);

    const API = getApiBase();
    
    // Fetch node data and fast heads/db snapshots
    Promise.all([
      fetch(`${API}/node_info.json`).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP error! status: ${r.status}`))),
      fetchNodeHeads(),
      fetchNodeDbInfo()
    ])
      .then(([nodeData]) => {
        console.log('Received node data:', nodeData);
        setNodeData(nodeData);
        setCachedData('/node_info.json', nodeData);
        const status = StatusEvaluator.nodes(nodeData, null);
        updatePageStatus('nodes', status);
      })

      .catch((err) => {
        console.error('Error fetching node data:', err);
        setError(err.message);
        updatePageStatus('nodes', 'red');
      })

      .finally(() => {
        setLoading(false);
        if (isInitialLoadRef.current) isInitialLoadRef.current = false;
      });
  }, [updatePageStatus, fetchNodeHeads, fetchNodeDbInfo, setCachedData]);

  useEffect(() => {
    console.log('NodesMobile component mounted, performing initial data fetch');
    try {
      const { cacheManager } = require('../../hooks/GlobalCacheManager');
      cacheManager.registerEndpoints('nodesmobile', [
        '/node_info.json',
        '/node_heads.json',
        '/node_db_info.json'
      ]);
      cacheManager.prewarmForPageId('nodesmobile').catch(() => {});
    } catch {}

    (async () => {
      try {
        const { globalCache } = require('../../hooks/useDataCache');
        const cachedNodeInfo = globalCache.get('/node_info.json');
        const cachedHeads = globalCache.get('/node_heads.json');
        const cachedDbInfo = globalCache.get('/node_db_info.json');
        if (cachedNodeInfo && typeof cachedNodeInfo === 'object') {
          setNodeData(cachedNodeInfo);
        }
        if (cachedHeads && typeof cachedHeads === 'object') {
          setNodeHeads(cachedHeads);
        }
        if (cachedDbInfo && typeof cachedDbInfo === 'object') {
          setNodeDbInfo(cachedDbInfo);
        }
        if (cachedNodeInfo) {
          setLoading(false);
          isInitialLoadRef.current = false;
        }
        try {
          if (cachedNodeInfo) {
            const st = StatusEvaluator.nodes(cachedNodeInfo, null);
            updatePageStatus('nodes', st);
          }
        } catch {}
      } catch {}

      fetchData();
    })();

    return () => {
      console.log('NodesMobile component unmounting, cleaning up state');
      setNodeData({});
      try {
        const { cacheManager } = require('../../hooks/GlobalCacheManager');
        cacheManager.unregisterEndpoints('nodesmobile');
      } catch {}
    };
  }, [fetchData, updatePageStatus]);

  // Chain tab draws exclusively from node_info.json. Database tab unions entities/networks
  // from node_info.json AND node_db_info.json so DB-only hosts (e.g. ENV_BACKUP, which runs
  // the FDC verifier DBs but no blockchain nodes) still render on the Database tab without
  // appearing on the Chain tab. Mirrors src/pages/nodes.js buildRows.
  const buildRows = useCallback((dataObj, mode, overrideDbInfo = null) => {
    const rows = [];
    const sourceDb = overrideDbInfo || nodeDbInfo;
    const safeData = (dataObj && typeof dataObj === 'object') ? dataObj : {};
    const safeDb = (sourceDb && typeof sourceDb === 'object') ? sourceDb : {};
    const allowedNetworksForDatabase = ['BITCOIN', 'DOGECOIN', 'XRPL'];

    const byEntity = new Map();
    const addNetworks = (entity, networks, filter = null) => {
      if (!entity || entity === 'timestamp' || !networks || typeof networks !== 'object') return;
      if (!byEntity.has(entity)) byEntity.set(entity, new Set());
      const bucket = byEntity.get(entity);
      Object.keys(networks).forEach((n) => {
        if (!filter || filter.includes(n)) bucket.add(n);
      });
    };

    Object.entries(safeData).forEach(([entity, networks]) => addNetworks(entity, networks));
    if (mode === 'database') {
      Object.entries(safeDb).forEach(([entity, networks]) => addNetworks(entity, networks, allowedNetworksForDatabase));
    }

    byEntity.forEach((networkSet, entity) => {
      networkSet.forEach((network) => {
        const nodeInfo = ((safeData[entity] || {})[network]) || null;

        if (mode === 'database' && !allowedNetworksForDatabase.includes(network)) return;
        if (mode === 'chain' && !nodeInfo) return;

        const { status = 'error', chain = null, database = null, type = 'FDC', ip_address = null, gc_name = null, simple_health = 'Unknown' } = nodeInfo || {};
        let rawDatum = mode === 'chain' ? chain : database;
        let datum = { ...(rawDatum || {}) };

        if (mode === 'database') {
          try {
            const light = ((safeDb[entity] || {})[network]) || null;
            if (light && typeof light === 'object') {
              datum = { ...datum, ...light };
            }
          } catch {}
        }

        const statusForMode = mode === 'database' ? (datum?.status ?? status) : status;
        const rowData = {
          entity,
          network,
          status: statusForMode,
          type,
          ip_address,
          gc_name,
          simple_health,
          ...(datum || {}),
        };
        if (mode === 'database' && chain?.latest_block) {
          rowData.chain_latest_block = chain.latest_block;
        }
        rows.push(rowData);
      });
    });

    rows.sort((a, b) => {
      const e = String(a.entity || '').localeCompare(String(b.entity || ''));
      if (e !== 0) return e;
      return String(a.network || '').localeCompare(String(b.network || ''));
    });
    return rows;
  }, [nodeDbInfo]);

  const checkAndFetchOnSse = useCallback(async () => {
    if (displayMode !== 'database') {
      fetchData();
      return;
    }
    try {
      const { cacheManager } = require('../../hooks/GlobalCacheManager');
      if (cacheManager.handleSSEUpdate) {
        await cacheManager.handleSSEUpdate({ source: 'nodesmobile' });
      }
    } catch {}
    try {
      const cachedInfo = getCachedData('/node_info.json');
      const cachedHeads = getCachedData('/node_heads.json');
      const cachedDb = getCachedData('/node_db_info.json');
      if (cachedInfo && typeof cachedInfo === 'object') setNodeData(cachedInfo);
      if (cachedHeads && typeof cachedHeads === 'object') setNodeHeads(cachedHeads);
      if (cachedDb && typeof cachedDb === 'object') setNodeDbInfo(cachedDb);
    } catch {}
    await Promise.all([fetchNodeDbInfo(), fetchNodeHeads()]);
  }, [displayMode, fetchData, getCachedData, fetchNodeDbInfo, fetchNodeHeads]);

  useDataUpdates(checkAndFetchOnSse);

  const calculateExternalValidation = useCallback((network, nodeData) => {
    const entityKey = nodeData?.entity;
    const networkKey = (network || '').toString().toUpperCase();
    const chainMapping = {
      'BITCOIN': 'BTC',
      'DOGECOIN': 'DOGE',
      'ETHEREUM': 'ETH',
      'FLARE': 'FLR',
      'FLARE_OBS': 'FLR',
      'FLARE_OBS2': 'FLR',
      'FLARE_VAL': 'FLR',
      'FLARE_VAL2': 'FLR',
      'SONGBIRD': 'SGB',
      'SONGBIRD_OBS': 'SGB',
      'SONGBIRD_OBS2': 'SGB',
      'XRPL': 'XRPL'
    };
    const extKey = chainMapping[networkKey] || 'DEFAULT';

    const compute = (extHeight, nodeHeight) => {
      const ext = typeof extHeight === 'string' ? parseInt(extHeight, 10) : extHeight;
      const nodeH = typeof nodeHeight === 'string' ? parseInt(nodeHeight, 10) : nodeHeight;
      if (!Number.isFinite(ext) || !Number.isFinite(nodeH)) return null;
      const blocksBehind = ext - nodeH;
      const th = EXTERNAL_VALIDATION_THRESHOLDS[extKey] || EXTERNAL_VALIDATION_THRESHOLDS.DEFAULT;
      let status;
      if (blocksBehind <= 0) status = 'synced';
      else if (blocksBehind <= th.GREEN_MAX) status = 'synced';
      else if (blocksBehind <= th.AMBER_MAX) status = 'behind';
      else status = 'significantly_behind';
      return { status, blocks_behind: blocksBehind, external_height: ext, node_height: nodeH };
    };

    const fast = nodeHeads?.[entityKey]?.[networkKey];
    if (fast && (fast.external_height != null || fast.node_height != null)) {
      const res = compute(fast.external_height, fast.node_height);
      if (res) return res;
    }
    const embedded = nodeData?.external_validation;
    if (embedded && (embedded.external_height != null || embedded.node_height != null)) {
      const res = compute(embedded.external_height, embedded.node_height);
      if (res) return res;
    }
    return { status: 'no_heads_data', blocks_behind: null, external_height: null };
  }, [nodeHeads]);

  useEffect(() => {
    if (displayMode !== 'chain') return;
    const next = buildRows(nodeData, 'chain');
    const prev = rowData;
    const changed = JSON.stringify(prev) !== JSON.stringify(next);
    if (changed) {
      setRowData(next);
      try {
        if (gridApiRef.current) {
          setTimeout(() => { try { gridApiRef.current.refreshCells({ force: true }); } catch {} }, 0);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData, nodeHeads, displayMode, buildRows]);

  useEffect(() => {
    if (displayMode !== 'database') return;
    const next = buildRows(nodeData, 'database');
    const prev = rowData;
    const changed = JSON.stringify(prev) !== JSON.stringify(next);
    if (changed) {
      setRowData(next);
      try {
        if (gridApiRef.current) {
          setTimeout(() => { try { gridApiRef.current.refreshCells({ force: true }); } catch {} }, 0);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData, nodeDbInfo, displayMode, buildRows]);

  const HealthStatusCellRenderer = useCallback((params) => {
    const row = params.data || {};
    const validation = calculateExternalValidation(row?.network, row);
    const statusLower = (row.status || '').toString().toLowerCase();
    
    let color = '#FFA500';
    
    // Force red for SYNCING/INDEXING regardless of external validation
    if (statusLower === 'syncing' || statusLower === 'indexing') {
      color = '#DC3545';
    } else if (validation && validation.status !== 'no_external_data' && validation.status !== 'no_node_height') {
      if (validation.status === 'synced') color = '#008000';
      else if (validation.status === 'behind') color = '#FFA500';
      else if (validation.status === 'significantly_behind') color = '#DC3545';
    } else {
      const statusStr = (row.status || '').toString().toUpperCase();
      if (statusStr === 'SYNCED') color = '#008000';
      else if (statusStr === 'BEHIND') color = '#FFA500';
      else color = '#DC3545';
    }
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
        <span style={{ color, fontSize: '20px' }}>●</span>
      </div>
    );
  }, [calculateExternalValidation]);

  const getColumnDefs = useCallback(() => {
    const commonColumns = [
      { headerName: '', field: 'simple_health', minWidth: 30, flex: 0.5, cellRenderer: HealthStatusCellRenderer, sortable: false,
        headerComponentParams: { template: '<div class="health-header"><img src="/assets/cloud.svg" alt="Node Status" title="Node Connection Status"/></div>' } },
      { field: 'status', headerName: 'Status', minWidth: 120, flex: 1, cellRenderer: NodeStatusCellRenderer,
        valueGetter: params => {
          const originalStatus = params.data?.status;
          const validation = calculateExternalValidation(params.data?.network, params.data);
          if (validation && validation.status !== 'no_heads_data' && validation.status !== 'no_node_height') {
            if (validation.status === 'significantly_behind') return `${validation.blocks_behind} BEHIND`;
            if (validation.status === 'behind') return `${validation.blocks_behind} BEHIND`;
            if (validation.status === 'synced') return 'SYNCED';
          }
          return originalStatus;
        } },
      { field: 'entity', headerName: 'Entity', minWidth: 100, flex: 1, cellRenderer: EntityCellRenderer },
      { field: 'network', headerName: 'Network', minWidth: 130, flex: 1,
        valueGetter: p => { const nk = p.data.network; return nk && nk.includes('_') ? nk.split('_')[0] : nk; },
        cellRenderer: NetworkCellRenderer },
      { field: 'type', headerName: 'Type', minWidth: 100, flex: 1 },
      { field: 'ip_address', headerName: 'IP', minWidth: 110, flex: 1 },
      { field: 'gc_name', headerName: 'GC NAME', minWidth: 210, flex: 1 },
    ];

    const chainColumns = [
      { field: 'external_validation', headerName: 'External Height', minWidth: 140, flex: 1,
        valueGetter: params => {
          const validation = calculateExternalValidation(params.data?.network, params.data);
          if (!validation || validation.status === 'no_heads_data') return 'No heads data';
          if (validation.status === 'no_node_height') return 'No node height';
          const externalHeight = validation.external_height;
          return externalHeight != null ? externalHeight.toString() : 'Unknown';
        } },
      { headerName: 'Latest Block', minWidth: 120, flex: 1, filter: 'agNumberColumnFilter',
        valueGetter: params => {
          const entity = params.data.entity;
          const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const prefer = (fast && typeof fast.node_height === 'number') ? fast.node_height : params.data.latest_block;
          const n = typeof prefer === 'string' ? parseInt(prefer, 10) : prefer;
          return isNaN(n) ? null : n;
        } },
      { headerName: 'Earliest Block', minWidth: 140, flex: 1, filter: 'agNumberColumnFilter',
        valueGetter: params => { const v = params.data.first_block ?? params.data.earliest_block; const n = typeof v === 'string' ? parseInt(v, 10) : v; return isNaN(n) ? null : n; } },
      { headerName: 'Total Blocks', minWidth: 140, flex: 1, filter: 'agNumberColumnFilter',
        valueGetter: params => {
          const entity = params.data.entity; const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const latestRaw = (fast && typeof fast.node_height === 'number') ? fast.node_height : params.data.latest_block;
          const earliestRaw = params.data.first_block ?? params.data.earliest_block;
          const latest = typeof latestRaw === 'string' ? parseInt(latestRaw, 10) : latestRaw;
          const earliest = typeof earliestRaw === 'string' ? parseInt(earliestRaw, 10) : earliestRaw;
          if (isNaN(latest) || isNaN(earliest)) return null; const total = latest - earliest; return total >= 0 ? total : null;
        } },
      { headerName: 'Difference', minWidth: 130, flex: 1, filter: 'agNumberColumnFilter',
        valueGetter: params => {
          const entity = params.data.entity; const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const validation = calculateExternalValidation(params.data?.network, params.data);
          const extRaw = (fast && fast.external_height != null) ? fast.external_height : (validation?.external_height ?? null);
          const latestRaw = (fast && fast.node_height != null) ? fast.node_height : params.data.latest_block;
          const ext = typeof extRaw === 'string' ? parseInt(extRaw, 10) : extRaw;
          const latest = typeof latestRaw === 'string' ? parseInt(latestRaw, 10) : latestRaw;

          if (!Number.isFinite(ext) || !Number.isFinite(latest)) return null; return Math.abs(ext - latest);
        } },
      { field: 'range', headerName: 'Range', minWidth: 120, flex: 1 },
      { field: 'earliest_block_time', headerName: 'Earliest Block Time', minWidth: 180, flex: 1.5 },
      { field: 'latest_block_time', headerName: 'Latest Block', minWidth: 150, flex: 2,
        cellStyle: params => {
          const value = params.value; const networkKey = params.data.network;
          if (typeof value !== 'string' || value === 'Unknown' || value === 'N/A') return null;
          try {
            const blockTimeMs = new Date(value.replace(' ', 'T') + 'Z').getTime();
            const diffMinutes = (Date.now() - blockTimeMs) / (1000 * 60);
            let thresholdConfig = NODE_THRESHOLDS.LATEST_BLOCK_TIME_MINUTES.DEFAULT;
            if (networkKey === 'BITCOIN' || networkKey === 'DOGECOIN') thresholdConfig = NODE_THRESHOLDS.LATEST_BLOCK_TIME_MINUTES.BTC_DOGE;
            if (diffMinutes > thresholdConfig.RED_ABOVE) return { color: '#DC3545' };
            if (diffMinutes > thresholdConfig.AMBER_ABOVE) return { color: '#FFA500' };
          } catch (e) { return null; }
          return null;
        } },
      { field: 'version', headerName: 'Version', minWidth: 80, flex: 1 },
      
    ];

    const databaseColumns = [
      { field: 'status', headerName: 'Status', minWidth: 120, flex: 1, cellRenderer: NodeStatusCellRenderer,
        valueGetter: params => {
          const networkKey = (params.data?.network || '').toString().toUpperCase();
          const status = (params.data?.status || '').toString().toLowerCase();
          const dbLatest = params.data?.latest_block; const chainLatest = params.data?.chain_latest_block;
          let baseStatus = params.data?.status;
          if (networkKey.startsWith('XRPL')) {
            if (status === 'indexed' || status === 'indexing') baseStatus = status;
            else if (status === 'syncing') baseStatus = 'indexing';
            else if (status === 'synced') baseStatus = 'indexed';
            else if (status.includes('error') || status === 'unreachable' || status === 'unknown' || status === '') baseStatus = 'ERROR';
          }
          if (dbLatest && chainLatest && !isNaN(dbLatest) && !isNaN(chainLatest)) {
            const blocksBehind = parseInt(chainLatest) - parseInt(dbLatest);
            if (blocksBehind <= 0) return networkKey.startsWith('XRPL') ? 'INDEXED' : 'SYNCED';
            return blocksBehind === 1 ? '1 BEHIND' : `${blocksBehind} BEHIND`;
          }
          return baseStatus;
        } },
      { field: 'earliest_block', headerName: 'Earliest', minWidth: 130, flex: 1, filter: 'agNumberColumnFilter' },
      { field: 'latest_block', headerName: 'Latest', minWidth: 130, flex: 1, filter: 'agNumberColumnFilter' },
      { field: 'total_blocks', headerName: 'Total', minWidth: 130, flex: 1, filter: 'agNumberColumnFilter' },
      { field: 'external_validation', headerName: 'Node Height', minWidth: 140, flex: 1,
        valueGetter: params => { const live = params.data?.node_height; return typeof live === 'number' ? live : 'Unknown'; } },
      { field: 'blocks_remaining', headerName: 'Remaining', minWidth: 100, flex: 1, filter: 'agNumberColumnFilter',
        valueGetter: p => { const dbLatest = p.data?.latest_block; const nodeHeight = p.data?.node_height; if (typeof dbLatest === 'number' && typeof nodeHeight === 'number') { const diff = nodeHeight - dbLatest; return diff >= 0 ? diff : 0; } return p.data?.blocks_remaining ?? null; } },
      { field: 'indexing_progress', headerName: 'Progress', minWidth: 150, flex: 1 },
      { field: 'earliest_db_time', headerName: 'Earliest DB Time', minWidth: 200, flex: 2 },
      { field: 'latest_db_time', headerName: 'Latest DB Time', minWidth: 200, flex: 2,
        cellStyle: params => {
          const value = params.value; const networkKey = params.data.network; if (typeof value !== 'string' || value === 'Unknown' || value === 'N/A') return null; try {
            const dbTimeMs = new Date(value.replace(' ', 'T') + 'Z').getTime(); const diffMinutes = (Date.now() - dbTimeMs) / (1000 * 60);
            let thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DEFAULT; const upper = (networkKey || '').toUpperCase();
            if (upper === 'BITCOIN' && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.BTC) thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.BTC;
            else if (NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES[upper]) thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES[upper];
            else if (upper.startsWith('DOGE') && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DOGE) thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DOGE;
            else if (upper.startsWith('XRPL') && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.XRPL) thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.XRPL;
            if (diffMinutes > thresholdConfig.RED_ABOVE) return { color: '#DC3545' }; if (diffMinutes > thresholdConfig.AMBER_ABOVE) return { color: '#FFA500' };
          } catch (e) { return null; }
          return null;
        } },
      { field: 'database_size', headerName: 'DB Size', minWidth: 150, flex: 1 },
    ];

    return displayMode === 'chain' ? [...commonColumns, ...chainColumns] : [...commonColumns.filter(c => !['simple_health','type','ip_address','gc_name','status'].includes(c.field)), ...databaseColumns];
  }, [displayMode, calculateExternalValidation, nodeHeads, HealthStatusCellRenderer]);

  useEffect(() => {
    if (gridApiRef.current) {
      setTimeout(() => {
        if (gridApiRef.current) {
          if (displayMode === 'chain') {
            gridApiRef.current.setColumnsPinned(['simple_health', 'entity', 'network'], 'left');
          } else {
            gridApiRef.current.setColumnsPinned(['entity', 'network'], 'left');
          }
        }
      }, 200);
    }
  }, [displayMode]);

  useEffect(() => {
    if (displayMode !== 'database') return;
    try {
      const { globalCache } = require('../../hooks/useDataCache');
      const cachedDb = globalCache.get('/node_db_info.json');
      const cachedHeads = globalCache.get('/node_heads.json');
      if (cachedDb && typeof cachedDb === 'object') setNodeDbInfo(cachedDb);
      if (cachedHeads && typeof cachedHeads === 'object') setNodeHeads(cachedHeads);
    } catch {}
    fetchNodeDbInfo();
    fetchNodeHeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode]);

  const defaultColDef = { sortable: true, resizable: false };
  const gridOptions = { suppressScrollOnNewData: true, suppressHorizontalScroll: false, domLayout: 'normal', getRowHeight: () => 50, suppressColumnVirtualisation: true };

  if (loading) return <LoadingAnimation />;
  if (error) return <div className="error-message">Error loading nodes: {error}</div>;
  if (Object.keys(nodeData).filter(key => key !== 'timestamp').length === 0 && !loading && !error) return null;

  return (
    <div className="main-wrapper nodes-page">
      <div className="nodes-wrapper">
        <div className="nodes-content-wrapper">
          <div className="view-toggle-container">
            <span className={`toggle-label ${displayMode === 'chain' ? 'active' : ''}`}>
              Chain
            </span>
            <div className="toggle-switch">
              <input 
                type="checkbox" 
                id="nodes-view-toggle" 
                checked={displayMode === 'database'}
                onChange={() => {
                  const newMode = displayMode === 'chain' ? 'database' : 'chain';
                  setDisplayMode(newMode);
                }}
              />
              <label htmlFor="nodes-view-toggle" />
            </div>
            <span className={`toggle-label ${displayMode === 'database' ? 'active' : ''}`}>
              Database
            </span>
          </div>
          <div className="node-grid-wrapper">
            <div className="ag-theme-alpine-dark custom-dark-theme" style={{ height: '100%', width: '100%' }}>
              <AgGridReact
                columnDefs={getColumnDefs()}
                rowData={rowData}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                deltaRowDataMode={displayMode === 'chain'}
                getRowId={(params) => `${params.data.entity}-${params.data.network}`}
                suppressAnimationFrame={true}
                suppressRowClickSelection={true}
                onGridReady={(params) => {
                  gridApiRef.current = params.api;
                  if (params.api) {
                    // Explicitly pin columns for chain mode (no sizeColumnsToFit to preserve widths)
                    if (displayMode === 'chain') {
                      setTimeout(() => {
                        params.api.setColumnsPinned(['simple_health', 'entity', 'network'], 'left');
                      }, 100);
                    } else {
                      setTimeout(() => {
                        params.api.setColumnsPinned(['entity', 'network'], 'left');
                      }, 100);
                    }
                  }
                }}
                onFirstDataRendered={(params) => { 
                  if(params.api) {
                    // Ensure columns are pinned after first render
                    if (displayMode === 'chain') {
                      setTimeout(() => {
                        params.api.setColumnsPinned(['simple_health', 'entity', 'network'], 'left');
                      }, 150);
                    } else {
                      setTimeout(() => {
                        params.api.setColumnsPinned(['entity', 'network'], 'left');
                      }, 150);
                    }
                  }
                }}
                domLayout="normal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NodesMobile; 